import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export function CameraCapture({ open, onClose, onCapture, title = "Ambil Foto" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("user");
  const [ready, setReady] = useState(false);
  const [shot, setShot] = useState(null);

  const stop = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  };

  const start = async () => {
    stop();
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Perangkat/browser ini tidak mendukung kamera");
      onClose();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setReady(true);
    } catch (e) {
      toast.error("Tidak bisa akses kamera. Izinkan akses kamera pada browser.");
      onClose();
    }
  };

  useEffect(() => {
    if (open && !shot) start();
    else if (!open) { stop(); setShot(null); }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setShot(c.toDataURL("image/jpeg", 0.9));
    stop();
  };

  const retake = () => { setShot(null); start(); };

  const usePhoto = async () => {
    const res = await fetch(shot);
    const blob = await res.blob();
    const file = new File([blob], `kamera-${Date.now()}.jpg`, { type: "image/jpeg" });
    onCapture(file);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="relative overflow-hidden rounded-lg bg-black aspect-[3/4] flex items-center justify-center">
          {shot
            ? <img src={shot} alt="hasil" className="h-full w-full object-cover" data-testid="cam-preview" />
            : <video ref={videoRef} playsInline muted className="h-full w-full object-cover" data-testid="cam-video" />}
        </div>
        <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
          {!shot ? (
            <>
              <Button variant="outline" onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} data-testid="cam-flip"><RefreshCw className="h-4 w-4 mr-1" /> Balik Kamera</Button>
              <Button className="bg-blue-800 hover:bg-blue-900" disabled={!ready} onClick={snap} data-testid="cam-snap"><Camera className="h-4 w-4 mr-1" /> Ambil</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={retake} data-testid="cam-retake">Ulangi</Button>
              <Button className="bg-blue-800 hover:bg-blue-900" onClick={usePhoto} data-testid="cam-use">Gunakan Foto</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
